import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import AnimatedSection from "@/components/AnimatedSection";
import { holidayListData, holidayNotesData } from "@/mocks/companyPageData";

const typeStyles: Record<
  string,
  { tile: string; badge: string; glow: string; dot: string; bar: string }
> = {
  National: {
    tile: "bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50",
    badge: "bg-accent-100 text-accent-800",
    glow: "bg-accent-200/50",
    dot: "bg-accent-500",
    bar: "bg-accent-500",
  },
  Festival: {
    tile: "bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50",
    badge: "bg-primary-100 text-primary-800",
    glow: "bg-primary-200/50",
    dot: "bg-primary-500",
    bar: "bg-primary-500",
  },
  Optional: {
    tile: "bg-secondary-50 text-secondary-600 group-hover:bg-secondary-500 group-hover:text-background-50",
    badge: "bg-secondary-100 text-secondary-800",
    glow: "bg-secondary-200/50",
    dot: "bg-secondary-500",
    bar: "bg-secondary-500",
  },
};

const legend = [
  { label: "National", dot: "bg-accent-500" },
  { label: "Festival", dot: "bg-primary-500" },
  { label: "Optional", dot: "bg-secondary-500" },
];

export default function HolidayListPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Company" },
          { label: "Holiday List" },
        ]}
      />

      <PageHero
        badge="Holiday List"
        headline="AstroJal Holiday Calendar 2026"
        subtext="Every holiday for the year in one clean list — national holidays, festivals and optional days the team can choose from."
        primaryCTA="Talk to Our Team"
        secondaryCTA="View Careers"
      />

      {/* Holiday list */}
      <section className="py-14 md:py-20 bg-background-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-accent-100/40 blur-3xl" />

        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                2026 Calendar
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                List of Holidays 2026
              </h2>
              <div className="w-16 h-1 gradient-bar rounded-full mx-auto mb-5" />
              <p className="text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
                We plan our delivery schedules around these dates so timelines stay realistic and our
                team gets time to rest and recharge. Festival dates follow the official government
                calendar and may be updated during the year.
              </p>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-700">
                  <i className="ri-calendar-2-line text-sm text-accent-600" />
                  {holidayListData.length} Holidays
                </span>
                {legend.map((item) => (
                  <span
                    key={item.label}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-700"
                  >
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {holidayListData.map((holiday, i) => {
              const style = typeStyles[holiday.type];
              return (
                <AnimatedSection key={`${holiday.name}-${holiday.month}-${holiday.day}`} delay={i * 55}>
                  <div className="group relative h-full flex flex-col p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-300/60 cursor-pointer overflow-hidden transition-all duration-400 card-lift">
                    <span
                      className={`absolute top-0 left-0 h-1 w-full ${style.bar} scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-400`}
                    />
                    <span
                      className={`absolute -top-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${style.glow}`}
                    />

                    {/* Icon + badge */}
                    <div className="relative flex items-start justify-between mb-5">
                      <div
                        className={`w-14 h-14 md:w-16 md:h-16 shrink-0 flex items-center justify-center rounded-2xl transition-all duration-400 group-hover:scale-110 group-hover:-rotate-6 ${style.tile}`}
                      >
                        <span className="w-8 h-8 flex items-center justify-center">
                          <i className={`${holiday.icon} text-2xl md:text-3xl`} />
                        </span>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap ${style.badge}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {holiday.type}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="relative flex items-baseline gap-1.5 mb-2">
                      <span className="text-3xl md:text-4xl font-extrabold font-heading text-foreground-950 leading-none">
                        {holiday.day}
                      </span>
                      <span className="text-sm font-bold uppercase tracking-wider text-foreground-500">
                        {holiday.month}
                      </span>
                      <span className="text-xs font-semibold text-foreground-400">2026</span>
                    </div>

                    {/* Name + weekday */}
                    <div className="relative mt-auto">
                      <h3 className="text-base md:text-lg font-bold text-foreground-950 font-heading">
                        {holiday.name}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1.5">
                        <i className="ri-calendar-2-line text-sm" />
                        {holiday.weekday}
                      </p>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* How we observe holidays */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-14">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                Good to Know
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                How We Observe Holidays
              </h2>
              <div className="w-16 h-1 gradient-bar rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {holidayNotesData.map((note, i) => (
              <AnimatedSection key={note.title} delay={i * 80}>
                <div className="group flex items-start gap-4 p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/50 transition-all duration-400 card-lift h-full">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${note.icon} text-lg`} />
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">{note.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{note.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Planning a Project Around the Calendar?"
        text="Share your timeline and we'll help you plan delivery around holidays and key dates so nothing slips."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to Our Team"
      />
    </PageLayout>
  );
}